// @ts-nocheck
import { useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Text,
  Group,
  SimpleGrid,
  Table,
  Button,
  Alert,
  Divider,
  Badge,
} from '@mantine/core';
import { IconCheck, IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { StatusBadge } from '../../components/StatusBadge';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { usePayrollRun, useApprovePayrollRun } from '../../hooks/usePayroll';
import { formatCurrency } from '../../utils/format';

interface PayStubSummary {
  id: string;
  employeeId: string;
  employeeName: string;
  grossPay: number;
  netPay: number;
  federalTax: number;
  stateTax: number;
  ssTax: number;
  medicareTax: number;
}

interface RunDetail {
  id: string;
  status: string;
  payPeriodId: string;
  totalEmployees: number;
  totalGross: number;
  totalNet: number;
  totalDeductions: number;
  totalEmployerCost: number;
  isDryRun: boolean;
  errors: string[];
  warnings: string[];
  payStubs: PayStubSummary[];
}

export default function PayrollRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: rawData, loading: isLoading } = usePayrollRun(id ?? '');
  const approveMutation = useApprovePayrollRun();

  const run = rawData as unknown as RunDetail | undefined;

  if (isLoading) {
    return <LoadingOverlay message="Loading payroll run..." />;
  }

  if (!run) {
    return (
      <Stack>
        <PageHeader
          title="Payroll Run Not Found"
          breadcrumbs={[{ label: 'Payroll', to: '/admin/workforce/payroll' }]}
        />
        <Text c="dimmed">The requested payroll run could not be found.</Text>
      </Stack>
    );
  }

  return (
    <Stack>
      <PageHeader
        title={`Payroll Run ${run.id}`}
        breadcrumbs={[
          { label: 'Payroll', to: '/admin/workforce/payroll' },
          { label: `Run ${run.id}` },
        ]}
        actions={
          <Group gap="sm">
            {run.status === 'preview' && (
              <Button
                color="green"
                icon={<IconCheck size={16} />}
                onClick={() => approveMutation.mutate({ id: run.id })}
                loading={approveMutation.isPending}
              >
                Approve
              </Button>
            )}
          </Group>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <StatCard
          title="Status"
          value={run.status.replace(/_/g, ' ').toUpperCase()}
          icon={<IconCheck size={24} />}
          color={run.status === 'completed' ? 'green' : 'blue'}
        />
        <StatCard
          title="Employees"
          value={String(run.totalEmployees)}
          icon={<IconCheck size={24} />}
          color="blue"
        />
        <StatCard
          title="Total Gross"
          value={formatCurrency(run.totalGross)}
          icon={<IconCheck size={24} />}
          color="green"
        />
        <StatCard
          title="Total Net"
          value={formatCurrency(run.totalNet)}
          icon={<IconCheck size={24} />}
          color="teal"
        />
      </SimpleGrid>

      {run.isDryRun && (
        <Alert icon={<IconAlertTriangle size={16} />} color="blue">
          This is a dry-run preview. No payroll has been committed.
        </Alert>
      )}

      {run.errors.length > 0 && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Errors">
          <Stack gap="xs">
            {run.errors.map((e, i) => (
              <Text key={i} size="sm">{e}</Text>
            ))}
          </Stack>
        </Alert>
      )}

      {run.warnings.length > 0 && (
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="Warnings">
          <Stack gap="xs">
            {run.warnings.map((w, i) => (
              <Text key={i} size="sm">{w}</Text>
            ))}
          </Stack>
        </Alert>
      )}

      <Paper withBorder p="lg" radius="md">
        <Text fw={600} size="lg" mb="md">Pay Stubs</Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Employee</Table.Th>
              <Table.Th ta="right">Gross Pay</Table.Th>
              <Table.Th ta="right">Federal Tax</Table.Th>
              <Table.Th ta="right">State Tax</Table.Th>
              <Table.Th ta="right">SS Tax</Table.Th>
              <Table.Th ta="right">Medicare</Table.Th>
              <Table.Th ta="right">Net Pay</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(run.payStubs ?? []).map((stub) => (
              <Table.Tr
                key={stub.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/payroll/pay-stubs/${stub.id}`)}
              >
                <Table.Td>{stub.employeeName}</Table.Td>
                <Table.Td ta="right">{formatCurrency(stub.grossPay)}</Table.Td>
                <Table.Td ta="right">{formatCurrency(stub.federalTax)}</Table.Td>
                <Table.Td ta="right">{formatCurrency(stub.stateTax)}</Table.Td>
                <Table.Td ta="right">{formatCurrency(stub.ssTax)}</Table.Td>
                <Table.Td ta="right">{formatCurrency(stub.medicareTax)}</Table.Td>
                <Table.Td ta="right" fw={600}>{formatCurrency(stub.netPay)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td fw={700}>Totals</Table.Td>
              <Table.Td ta="right" fw={700}>{formatCurrency(run.totalGross)}</Table.Td>
              <Table.Td colSpan={4} />
              <Table.Td ta="right" fw={700}>{formatCurrency(run.totalNet)}</Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Paper>
    </Stack>
  );
}
